import { api } from './index';
import { 
  ApiResponse,
  Notification,
  ServiceProviderBase,
  ServiceProviderDetail,
  ServiceProviderDetailResponse,
  ServiceProviderListResponse,
  ServiceProviderStatusResponse,
  IndividualContract
} from '../types';

export const serviceProviderService = {
  getServiceProviders: async (): Promise<ServiceProviderBase[]> => {
    const response = await api.get<ServiceProviderListResponse>('/service-providers');
    return response.data.data;
  },

  getServiceProviderDetails: async (id: string): Promise<ServiceProviderDetail> => {
    const response = await api.get<ServiceProviderDetailResponse>(`/service-providers/${id}`);
    return response.data.data;
  },

  updateServiceProviderStatus: async (id: string, status: ServiceProviderBase['status']): Promise<ServiceProviderBase> => {
    const response = await api.patch<ServiceProviderStatusResponse>(`/service-providers/${id}/status`, { status });
    return response.data.data;
  },

  getIndividualContractDetail: async (providerId: string, contractId: string): Promise<IndividualContract> => {
    const response = await api.get<ApiResponse<IndividualContract>>(`/service-providers/${providerId}/contracts/${contractId}`);
    return response.data.data;
  },

  analyzeAllServiceProviders: async (providerIds: string[]): Promise<Notification> => {
    const response = await api.post('/service-providers/analyze-all', { ids: providerIds });
    return response.data;
  },

  analyzeAllIndividualContracts: async (providerId: string, contractIds: string[]): Promise<Notification> => {
    const response = await api.post(`/service-providers/${providerId}/contracts/analyze-all`, { ids: contractIds });
    return response.data;
  },

  analyzeIndividualContract: async (providerId: string, contractId: string): Promise<Notification> => {
    const response = await api.post(`/service-providers/${providerId}/contracts/${contractId}/analyze`);
    return response.data;
  },

  cancelIndividualContractAnalysis: async (providerId: string, contractId: string): Promise<Notification> => {
    const response = await api.post(`/service-providers/${providerId}/contracts/${contractId}/analyze/cancel`);
    return response.data;
  },

  deleteIndividualContract: async (providerId: string, contractId: string): Promise<Notification> => {
    const response = await api.delete(`/service-providers/${providerId}/contracts/${contractId}`);
    return response.data;
  },

  deleteMultipleIndividualContracts: async (providerId: string, contractIds: string[]): Promise<Notification> => {
    const response = await api.delete(`/service-providers/${providerId}/contracts`, { data: { ids: contractIds } });
    return response.data;
  },

  addDocuments: async (providerId: string, contractId: string, formData: FormData): Promise<Notification> => {
    const response = await api.post(`/service-providers/${providerId}/contracts/${contractId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
    });
    return response.data;
  },

  deleteDocument: async (providerId: string, contractId: string, documentId: string): Promise<Notification> => {
    const response = await api.delete(`/service-providers/${providerId}/contracts/${contractId}/documents/${documentId}`);
    return response.data;
  },

  deleteMultipleDocuments: async (providerId: string, contractId: string, documentIds: string[]): Promise<Notification> => {
    const response = await api.delete(`/service-providers/${providerId}/contracts/${contractId}/documents`, { data: { ids: documentIds } });
    return response.data;
  },

  downloadIndividualContract: async (providerId: string, contractId: string): Promise<Blob> => {
    const response = await api.get(`/service-providers/${providerId}/contracts/${contractId}/download`, {
      responseType: 'blob', headers: { 'Accept': 'application/pdf' }
    });
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vertrag_${contractId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return blob;
  },

  editIndividualContractTitle: async (providerId: string, contractId: string, title: string): Promise<Notification> => {
    const response = await api.patch(`/service-providers/${providerId}/contracts/${contractId}/title`, { title });
    return response.data;
  },

  editServiceProviderTitle: async (providerId: string, title: string): Promise<Notification> => {
    const response = await api.patch(`/service-providers/${providerId}/title`, { title });
    return response.data;
  },

  addIndividualContract: async (providerId: string, file: File, serviceName: string, isCritical: boolean): Promise<Notification> => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('serviceName', serviceName);
    formData.append('isCritical', isCritical.toString());
    const response = await api.post(`/service-providers/${providerId}/contracts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
    });
    return response.data;
  },

  addMultipleIndividualContracts: async (providerId: string, files: File[], serviceName: string, isCritical: boolean = true): Promise<Notification> => {
    const formData = new FormData();
    files.forEach((file) => { formData.append('files', file); });
    formData.append('serviceName', serviceName);
    formData.append('isCritical', isCritical.toString());
    const response = await api.post(`/service-providers/${providerId}/contracts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
    });
    return response.data;
  },

  bulkUploadContracts: async (providerId: string, zipFile: File): Promise<Notification> => {
    const formData = new FormData();
    formData.append('file', zipFile);
    const response = await api.post(`/service-providers/${providerId}/contracts/zip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json' }
    });
    return response.data;
  },

  downloadContractDocument: async (providerId: string, contractId: string, documentId: string): Promise<Blob> => {
    const response = await api.get(`/service-providers/${providerId}/contracts/${contractId}/documents/${documentId}/download`, {
      responseType: 'blob', headers: { 'Accept': 'application/pdf' }
    });
    return response.data;
  },

  deleteServiceProvider: async (providerId: string): Promise<Notification> => {
    const response = await api.delete(`/service-providers/${providerId}`);
    return response.data;
  },

  deleteMultipleServiceProviders: async (providerIds: string[]): Promise<Notification> => {
    const response = await api.delete('/service-providers', { data: { ids: providerIds } });
    return response.data;
  },

  saveIndividualContractNote: async (providerId: string, contractId: string, note: string): Promise<Notification> => {
    const response = await api.patch(`/service-providers/${providerId}/contracts/${contractId}/note`, { note });
    return response.data;
  },

  createServiceProvider: async (title: string): Promise<{ status: string; message: string; data: ServiceProviderBase; }> => {
    const response = await api.post('/service-providers', { title });
    return response.data;
  },

  downloadContractResult: async (providerId: string, contractId: string, resultId: string): Promise<Blob> => {
    const response = await api.get(`/service-providers/${providerId}/contracts/${contractId}/results/${resultId}/download`, {
      responseType: 'blob', headers: { 'Accept': 'application/pdf' }
    });
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Analyseergebnis_${contractId}_${resultId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return blob;
  },

  downloadContractResultsExcel: async (providerId: string, contractId: string): Promise<Blob> => {
    const response = await api.get(`/service-providers/${providerId}/contracts/${contractId}/results/excel`, {
      responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    });
    const blob = response.data;
    const disposition: string | undefined = response.headers['content-disposition'];
    let filename = `Analyseergebnisse_${contractId}.xlsx`;
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) filename = decodeURIComponent(match[1]);
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return blob;
  },

  downloadContractAddendum: async (providerId: string, contractId: string): Promise<Blob> => {
    const response = await api.get(`/service-providers/${providerId}/contracts/${contractId}/addendum`, {
      responseType: 'blob', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
    });
    const blob = response.data;
    const disposition: string | undefined = response.headers['content-disposition'];
    let filename = `Ergaenzungsvereinbarung_${contractId}.docx`;
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) filename = decodeURIComponent(match[1]);
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return blob;
  },

  updateIndividualContractCriticality: async (providerId: string, contractId: string, isCritical: boolean): Promise<any> => {
    const response = await api.patch(`/service-providers/${providerId}/contracts/${contractId}/criticality`, { isCritical });
    return response.data;
  },
  
  // Neuen Analyse-Typ für eine Dienstleistung setzen
  updateIndividualContractAnalysisType: async (providerId: string, contractId: string, analysisType: string): Promise<any> => {
    const response = await api.patch(`/service-providers/${providerId}/contracts/${contractId}/analysis-type`, { analysisType });
    return response.data;
  },
};



